<template>
  <v-row class="user-bio-panel">
    <!-- user profile -->
    <v-col cols="12">
      <v-card class="pt-10">
        <v-card-title
          v-touch="{
            left: () =>
              goToNext(employeeData.employeeIds, employeeData.id, loading, {
                name: 'staff-employee-view',
                param: 'id',
              }),
            right: () =>
              goToPrevious(employeeData.employeeIds, employeeData.id, loading, {
                name: 'staff-employee-view',
                param: 'id',
              }),
          }"
          class="justify-center flex-column"
        >
          <div class="d-flex justify-space-around align-center w-full">
            <v-btn
              icon
              color="primary"
              :disabled="!canGoToPrevious(employeeData.employeeIds, employeeData.id) || loading"
              @click="
                goToPrevious(employeeData.employeeIds, employeeData.id, loading, {
                  name: 'staff-employee-view',
                  param: 'id',
                })
              "
              ><v-icon>{{ icons.mdiChevronLeft }}</v-icon></v-btn
            >
            <v-avatar
              :color="employeeData.avatar ? '' : 'primary'"
              :class="employeeData.avatar ? '' : 'v-avatar-light-bg primary--text'"
              size="120"
              rounded
              class="mb-4"
            >
              <v-img
                v-if="employeeData.avatar"
                :src="require(`@/assets/images/avatars/${employeeData.avatar}`)"
              ></v-img>
              <span v-else class="font-weight-semibold text-5xl">{{
                avatarText(`${employeeData.firstName} ${employeeData.lastName}`)
              }}</span>
            </v-avatar>
            <v-btn
              icon
              color="primary"
              :disabled="!canGoToNext(employeeData.employeeIds, employeeData.id) || loading"
              @click="
                goToNext(employeeData.employeeIds, employeeData.id, loading, {
                  name: 'staff-employee-view',
                  param: 'id',
                })
              "
            >
              <v-icon>{{ icons.mdiChevronRight }}</v-icon>
            </v-btn>
          </div>

          <span class="mb-2">{{ employeeData.firstName }} {{ employeeData.lastName }}</span>
          <v-chip
            label
            small
            color="primary"
            class="v-chip-light-bg primary--text font-weight-semibold text-capitalize ma-1 mb-2"
            v-text="statusText"
          />
          <span class="d-flex justify-center align-center flex-wrap">
            <v-chip
              v-for="(tag, index) in employeeData.tags"
              :key="index"
              label
              small
              color="info"
              outlined
              class="font-weight-semibold ma-1"
              v-text="tag.tag"
            />
          </span>
        </v-card-title>

        <v-card-text>
          <h2 v-t="'details'" class="text-xl font-weight-semibold mb-2" />

          <v-divider></v-divider>

          <v-list>
            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('firstName', { postfix: '' }) }}:</span>
              <span class="text--secondary">{{ employeeData.firstName }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('lastName', { postfix: '' }) }}:</span>
              <span class="text--secondary">{{ employeeData.lastName }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium text-no-wrap me-2">{{ $t('nationality') }}:</span>
              <span class="text--secondary">{{ emptyValueFormatter(employeeData.nationality) }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium text-no-wrap me-2">{{ $t('employeeStatus') }}:</span>
              <v-chip
                v-if="statusText"
                label
                small
                color="primary"
                class="v-chip-light-bg primary--text font-weight-semibold text-capitalize"
                v-text="statusText"
              />
              <span v-else class="text--secondary">-/-</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium text-no-wrap me-2">{{ $t('position') }}:</span>
              <span v-if="employeeData.assignedPositions && employeeData.assignedPositions.length" class="text--secondary">
                <v-chip
                  v-for="pos in employeeData.assignedPositions"
                  :key="pos.id"
                  small
                  label
                  color="secondary"
                  class="v-chip-light-bg secondary--text font-weight-medium text-capitalize ma-1"
                >
                  {{ pos.positionName }} ({{ pos.projectName }})
                </v-chip>
              </span>
              <span v-else class="text--secondary">-/-</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('startWorkingDay') }}:</span>
              <span class="text--secondary">{{ formattedWorkingStartDay }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium text-no-wrap me-2">{{ $t('eighteenMonthsDeadline') }}:</span>
              <span class="text--secondary">{{ eighteenMonthsDeadline }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium text-no-wrap me-2">{{ $t('accommodation') }}:</span>
              <span v-if="accommodationText" class="text--secondary">{{ accommodationText }}</span>
              <span v-else class="text--secondary">-/-</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('language') }}:</span>
              <span class="text--secondary">
                <v-chip
                  v-for="lang in languageTexts"
                  :key="lang.value"
                  small
                  label
                  color="secondary"
                  class="v-chip-light-bg secondary--text font-weight-medium text-capitalize ma-1"
                  v-text="lang.text"
                />
                <span v-if="!languageTexts.length">-/-</span>
              </span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium text-no-wrap me-2">{{ $t('email', { postfix: '' }) }}:</span>
              <span class="text--secondary">{{ emptyValueFormatter(employeeData.email) }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium text-no-wrap me-2">{{ $t('phoneNumber') }}:</span>
              <span class="text--secondary">{{ emptyValueFormatter(employeeData.phone) }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium text-no-wrap me-2">{{ $t('martialStatus') }}:</span>
              <span class="text--secondary">{{ emptyValueFormatter(employeeData.maritalStatus) }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium text-no-wrap me-2">{{ $t('kids') }}:</span>
              <span class="text--secondary">{{ employeeData.kids ? $t('yes') : $t('no') }}</span>
            </v-list-item>

            <template v-if="pinnedDetailsFields.length">
              <v-divider class="my-2" />
              <v-list-item
                v-for="cf in pinnedDetailsFields"
                :key="cf.id"
                dense
                class="px-0 my-n2"
              >
                <span class="font-weight-medium text-no-wrap me-2">{{ cf.name }}:</span>
                <span class="text--secondary">{{ formatCustomFieldDisplayValue(cf, $t) }}</span>
              </v-list-item>
            </template>
          </v-list>

          <template v-if="pinnedCustomFieldsBySection.length">
            <template v-for="section in pinnedCustomFieldsBySection">
              <h3 :key="`section-heading-${section.name}`" class="text-lg font-weight-semibold mt-4 mb-2">
                {{ section.name }}
              </h3>
              <v-divider :key="`section-divider-${section.name}`" class="mb-2" />
              <v-list :key="`section-list-${section.name}`">
                <v-list-item
                  v-for="cf in section.fields"
                  :key="cf.id"
                  dense
                  class="px-0 my-n2"
                >
                  <span class="font-weight-medium text-no-wrap me-2">{{ cf.name }}:</span>
                  <span class="text--secondary">{{ formatCustomFieldDisplayValue(cf, $t) }}</span>
                </v-list-item>
              </v-list>
            </template>
          </template>
        </v-card-text>

        <v-card-actions class="justify-center">
          <v-row justify="center">
            <v-col v-if="$can(ACTIONS.DELETE, employeeData)" cols="auto">
              <v-dialog v-model="isDeleteDialogOpen" max-width="650px" persistent>
                <template #activator="{ on, attrs }">
                  <v-btn color="error" v-bind="attrs" v-on="on" @click="isDeleteDialogOpen = true">
                    <v-icon class="me-2">{{ icons.mdiTrashCanOutline }}</v-icon>
                    <span v-t="'delete'" />
                  </v-btn>
                </template>
                <v-card class="pa-sm-10 pa-3">
                  <v-card-title v-t="'delete'" class="justify-center text-h5" />
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" class="d-flex align-center justify-center">{{
                        $t('confirmationModalUndoneQuestion')
                      }}</v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" class="d-flex justify-center mt-3">
                        <v-btn v-t="'discard'" outlined class="me-3" @click="isDeleteDialogOpen = false" />
                        <v-btn v-t="'delete'" color="error" @click="deleteEmployeeHandler" />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-dialog>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mdiTrashCanOutline, mdiChevronLeft, mdiChevronRight } from '@mdi/js'
import { avatarText, kFormatter, formatDateToMonthShort, emptyValueFormatter, formatDate } from '@core/utils/filter'
import { ref, onMounted, computed } from '@vue/composition-api'
import { useActions, useState } from 'vuex-composition-helpers'
import { useRouter } from '@core/utils'
import useEmployeeList from '../../employee-list/useEmployeeList'
import employeeUtils from '@/utils/employeeUtils'
import { ACTIONS, PRIVATE } from '@/plugins/acl/const'
import navigationUtils from '@/utils/navigationUtils'
import {
  getPinnedCustomFieldsBySection,
  getPinnedDetailsFields,
  safeFormatDate,
  formatCustomFieldDisplayValue,
} from '@/utils/customFieldUtils'

export default {
  props: {
    loading: {
      type: Boolean,
      required: true,
    },
  },
  setup() {
    const { deleteEmployee } = useActions('staff', ['deleteEmployee'])
    const { employeeData } = useState('staff', ['employeeData'])

    const isDeleteDialogOpen = ref(false)
    const { router } = useRouter()

    const deleteEmployeeHandler = async () => {
      await deleteEmployee(employeeData.value.id)
      await router.push({ name: 'staff-employee-list' })
    }

    const { resolveUserAccessVariant, resolveUserStatusVariant } = useEmployeeList()

    const {
      fetchLanguageChoices,
      fetchStatusChoices,

      languageChoices,
      statusChoices,
    } = employeeUtils()

    const languageTexts = computed(() => {
      return languageChoices.value.filter((lang) => employeeData.value.languages.includes(lang.value))
    })
    const statusText = computed(
      () => statusChoices.value.find((status) => status.value === employeeData.value.status)?.text,
    )

    const formattedWorkingStartDay = computed(() => {
      const startDate = employeeData.value.workingStartDay
      if (!startDate) return '-/-'
      return formatDate(startDate)
    })

    const eighteenMonthsDeadline = computed(() => {
      const startDate = employeeData.value.workingStartDay
      if (!startDate) return '-/-'
      const deadline = new Date(startDate)
      deadline.setMonth(deadline.getMonth() + 18)
      return formatDate(deadline.toISOString())
    })

    const accommodationText = computed(() => {
      const occupants = employeeData.value.occupants
      if (occupants && occupants.length) {
        return occupants.map(o => `${o.roomName} - ${o.address}`).join(', ')
      }
      const customAddress = employeeData.value.customAddress
      if (customAddress && customAddress.length) {
        return customAddress.map(a => a.address).join(', ')
      }
      return null
    })

    const pinnedDetailsFields = computed(() => {
      return getPinnedDetailsFields(employeeData.value.customFields || [], {
        excludeScope: 'candidate',
      })
    })

    const pinnedCustomFieldsBySection = computed(() => {
      return getPinnedCustomFieldsBySection(employeeData.value.customFields || [], {
        excludeScope: 'candidate',
      })
    })

    const { canGoToNext, goToNext, canGoToPrevious, goToPrevious } = navigationUtils()

    onMounted(async () => {
      await fetchStatusChoices()
      await fetchLanguageChoices()
    })

    return {
      resolveUserAccessVariant,
      resolveUserStatusVariant,
      avatarText,
      kFormatter,
      formatDateToMonthShort,
      emptyValueFormatter,
      safeFormatDate,
      formatCustomFieldDisplayValue,

      canGoToNext,
      goToNext,
      canGoToPrevious,
      goToPrevious,

      isDeleteDialogOpen,
      deleteEmployeeHandler,

      languageTexts,
      statusText,
      formattedWorkingStartDay,
      eighteenMonthsDeadline,
      accommodationText,
      pinnedDetailsFields,
      pinnedCustomFieldsBySection,

      employeeData,

      languageChoices,
      statusChoices,
      icons: {
        mdiTrashCanOutline,
        mdiChevronLeft,
        mdiChevronRight,
      },
      ACTIONS,
      PRIVATE,
    }
  },
}
</script>
